export default function IndianBreakfastNearMe() {
  const faqSchema = `{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is Indian breakfast spicy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Not necessarily. Dishes like idli, dosa, and poha are mild on their own — the heat, if any, comes from the chutneys or pickles served alongside, which you can always ask to have on the side."
        }
      },
      {
        "@type": "Question",
        "name": "Is Indian breakfast vegetarian-friendly?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Almost entirely, yes. The vast majority of traditional Indian breakfast dishes — idli, dosa, upma, poha, paratha — are vegetarian by default, which makes it one of the easiest cuisines to order breakfast from as a group with mixed dietary preferences."
        }
      },
      {
        "@type": "Question",
        "name": "Can I order Indian breakfast for pickup?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes — Biryani2Go supports pickup and delivery ordering through our menu page. Order ahead and it's ready when you arrive."
        }
      }
    ]
  }`;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
      <div className="space-y-6 text-base md:text-lg leading-relaxed text-ink-2 max-w-[68ch]">
        <p>
          If you've typed "Indian breakfast near me" into Google before 9am, you already know the
          problem: most search results point you to dinner menus with a "breakfast" tag slapped on,
          or to a diner serving eggs and toast with a side of confusion about what "Indian breakfast"
          even means. Real Indian breakfast is its own category of food — built for filling you up
          without weighing you down, and built to be eaten fast on the way out the door or slow on a
          lazy weekend morning.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          What Actually Counts as Indian Breakfast?
        </h2>
        <p>
          Depending on which part of India you're talking about, breakfast looks different. South
          Indian mornings run on steamed and fermented rice-and-lentil dishes — think idli, dosa, and
          upma — light on oil, easy on the stomach, and almost always served with a tempered lentil
          soup called sambar and a coconut chutney on the side. North Indian breakfast leans heartier:
          stuffed parathas, poha, or spiced chickpeas with puffed bread (chole bhature on a lazy
          Sunday). What ties both traditions together is freshness — Indian breakfast food is rarely
          meant to sit around. It's cooked close to when it's eaten.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Why Most "Indian Breakfast" Options Fall Short
        </h2>
        <p>
          A lot of restaurants treat breakfast as an afterthought — pre-made trays sitting under a
          heat lamp since 7am, reheated on demand. That works for some cuisines. It doesn't work for
          Indian food, where texture is everything: a dosa needs to be crisp right off the griddle, and
          parathas lose their layers the second they're steamed in a warmer instead of served fresh off
          the tawa.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          How Biryani2Go Approaches Breakfast
        </h2>
        <p>
          We built our name on dum-cooked biryani — the slow, sealed, steamed-for-hours method that
          doesn't cut corners. We bring that same standard to breakfast. Nothing sits under a heat
          lamp. Nothing gets reheated twice. If it's on the morning menu, it's made when you order it,
          the same way our biryani is.
        </p>
        <p>
          Our breakfast menu is still growing — check the{' '}
          <a href="/menu" className="text-ember font-semibold underline hover:no-underline">
            full menu
          </a>{' '}
          for what's available right now, since our morning offerings expand as we settle into new
          markets. If you don't see a specific breakfast dish yet, reach out on the{' '}
          <a href="/contact" className="text-ember font-semibold underline hover:no-underline">
            Contact page
          </a>{' '}
          — we're actively building this part of the menu based on what people are asking for.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Frequently Asked Questions
        </h2>

        <div className="space-y-5">
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              Is Indian breakfast spicy?
            </h3>
            <p>
              Not necessarily. Dishes like idli, dosa, and poha are mild on their own — the heat, if any,
              comes from the chutneys or pickles served alongside, which you can always ask to have on
              the side.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              Is Indian breakfast vegetarian-friendly?
            </h3>
            <p>
              Almost entirely, yes. The vast majority of traditional Indian breakfast dishes — idli,
              dosa, upma, poha, paratha — are vegetarian by default, which makes it one of the easiest
              cuisines to order breakfast from as a group with mixed dietary preferences.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              Can I order Indian breakfast for pickup?
            </h3>
            <p>
              Yes — Biryani2Go supports pickup and delivery ordering through our{' '}
              <a href="/menu" className="text-ember font-semibold underline hover:no-underline">
                menu page
              </a>
              . Order ahead and it's ready when you arrive.
            </p>
          </div>
        </div>

        <p className="!mt-10 text-sm text-ink-2/80 italic">
          Menu availability, hours, and locations are updated regularly — always check the current{' '}
          <a href="/menu" className="text-ember underline hover:no-underline">menu</a> and{' '}
          <a href="/contact" className="text-ember underline hover:no-underline">contact page</a> for
          the latest details.
        </p>
      </div>
    </>
  );
}
